import { ColumnDef } from "@tanstack/react-table";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Edit,
  KeyRound,
  Plus,
  Shield,
  Trash2,
  UserCog,
  Users,
  X,
} from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, Redirect, useLocation } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { rbacKeys } from "@/constant/queryKeys";
import { useToast } from "@/hooks/use-toast";
import { apiRequestV2, parseNormalizedError, queryClient } from "@/lib/queryClient";
import { getPartnerAdminListOptions } from "@/services/queryOptions/partnerManagementQueryOptions";
import {
  clearRbacCacheOptions,
  createBulkWhitelistOptions,
  getRbacPartnerAdminsOptions,
  getRbacPermissionsOptions,
  getRbacRolePermissionsOptions,
  getRbacRolesOptions,
  getRbacUserPartnersOptions,
  getRbacUserRolesOptions,
  getRbacUsersOptions,
  getRbacWhitelistOptions,
  invalidateRbac,
} from "@/services/queryOptions/rbacQueryOptions";
import type { OuraListParams } from "@/types/oura";
import type { PartnerAdmin } from "@/types/partner-management";
import type { RbacPermission, RbacRole, RbacUser, RbacWhitelistEntry } from "@/types/rbac";
import { AdminDataTable } from "../components/AdminDataTable";

const rbacNav = [
  { href: "/tweeto-admin/rbac/users", label: "Users & Roles", icon: Users },
  { href: "/tweeto-admin/rbac/roles", label: "Roles & Permissions", icon: Shield },
  { href: "/tweeto-admin/rbac/partners", label: "Partner Admins", icon: UserCog },
  { href: "/tweeto-admin/rbac/whitelist", label: "Role Whitelist", icon: KeyRound },
];

function formatDate(value?: string | null) {
  return value ? new Date(value).toLocaleString() : "-";
}

function errorMessage(error: unknown) {
  return parseNormalizedError(error).message;
}

function RoleBadges({ roles }: { roles: RbacRole[] }) {
  if (!roles.length) return <span className="text-zinc-500">No roles</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {roles.map((role) => (
        <Badge key={role.roleName} variant={role.roleName === "system_admin" ? "default" : "outline"}>
          {role.roleName}
        </Badge>
      ))}
    </div>
  );
}

function SectionNav() {
  const [location] = useLocation();

  return (
    <div className="flex flex-wrap gap-2">
      {rbacNav.map((item) => {
        const Icon = item.icon;
        const active = location === item.href;
        return (
          <Link key={item.href} href={item.href}>
            <Button variant={active ? "default" : "outline"} size="sm">
              <Icon className="mr-2 h-4 w-4" />
              {item.label}
            </Button>
          </Link>
        );
      })}
    </div>
  );
}

function UsersAndRolesPage() {
  const { toast } = useToast();
  const [params, setParams] = useState<OuraListParams>({ page: 1, page_size: 25, sort: "id", order: "desc" });
  const [selectedUser, setSelectedUser] = useState<RbacUser | null>(null);
  const [roleName, setRoleName] = useState("");
  const users = useQuery(getRbacUsersOptions(params));
  const roles = useQuery(getRbacRolesOptions());
  const userRoles = useQuery(getRbacUserRolesOptions(selectedUser?.id));
  const userPartners = useQuery(getRbacUserPartnersOptions(selectedUser?.id));

  const assignRole = useMutation({
    mutationFn: () => apiRequestV2("POST", `/api/v1/rbac/users/${selectedUser?.id}/roles`, { roleName }),
    onSuccess: () => {
      toast({ title: "Role assigned" });
      setRoleName("");
      invalidateRbac();
    },
    onError: (error) => toast({ title: "Cannot assign role", description: errorMessage(error), variant: "destructive" }),
  });

  const removeRole = useMutation({
    mutationFn: ({ userId, nextRoleName }: { userId: number; nextRoleName: string }) =>
      apiRequestV2("DELETE", `/api/v1/rbac/users/${userId}/roles/${encodeURIComponent(nextRoleName)}`),
    onSuccess: () => {
      toast({ title: "Role removed" });
      invalidateRbac();
    },
    onError: (error) => toast({ title: "Cannot remove role", description: errorMessage(error), variant: "destructive" }),
  });

  const invalidateUserCache = useMutation({
    mutationFn: (userId: number) => apiRequestV2("POST", `/api/v1/rbac/cache/invalidate/${userId}`),
    onSuccess: () => toast({ title: "User RBAC cache invalidated" }),
    onError: (error) => toast({ title: "Cannot invalidate cache", description: errorMessage(error), variant: "destructive" }),
  });

  const columns = useMemo<ColumnDef<RbacUser>[]>(() => [
    { accessorKey: "id", header: "ID" },
    {
      accessorKey: "username",
      header: "User",
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          {row.original.profileImage ? (
            <img src={row.original.profileImage} alt="" className="h-9 w-9 rounded object-cover" />
          ) : (
            <div className="flex h-9 w-9 items-center justify-center rounded bg-zinc-800 text-xs">
              {row.original.username.slice(0, 2).toUpperCase()}
            </div>
          )}
          <div>
            <div className="font-medium">{row.original.name || row.original.username}</div>
            <div className="text-xs text-zinc-500">@{row.original.username}</div>
          </div>
        </div>
      ),
    },
    {
      id: "providers",
      header: "Providers",
      cell: ({ row }) => (
        <div className="flex gap-1">
          {row.original.xUserId && <Badge variant="outline">X</Badge>}
          {row.original.googleUserId && <Badge variant="outline">Google</Badge>}
          {!row.original.xUserId && !row.original.googleUserId && "-"}
        </div>
      ),
    },
    {
      id: "roles",
      header: "Roles",
      cell: ({ row }) => <RoleBadges roles={row.original.roles} />,
    },
    { accessorKey: "createdAt", header: "Created", cell: ({ row }) => formatDate(row.original.createdAt) },
  ], []);

  const assignedRoles = userRoles.data?.roles ?? selectedUser?.roles ?? [];
  const assignableRoles = (roles.data?.roles ?? []).filter(
    (role) => !assignedRoles.some((assigned) => assigned.roleName === role.roleName),
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Users & Roles</h2>
        <p className="text-sm text-zinc-400">Search users, inspect roles, and update role assignments.</p>
      </div>
      <Card>
        <CardContent className="pt-6">
          <AdminDataTable
            columns={columns}
            data={users.data?.items ?? []}
            total={users.data?.total ?? 0}
            params={params}
            onParamsChange={setParams}
            exportName="rbac-users.csv"
            searchPlaceholder="Search id, username, name, provider id..."
            onRowClick={setSelectedUser}
          />
        </CardContent>
      </Card>
      <Sheet open={Boolean(selectedUser)} onOpenChange={(open) => !open && setSelectedUser(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>{selectedUser?.name || selectedUser?.username}</SheetTitle>
          </SheetHeader>
          {selectedUser && (
            <div className="mt-6 space-y-6">
              <div className="rounded border border-zinc-800 p-4">
                <div className="text-sm text-zinc-500">User</div>
                <div className="mt-1 font-mono text-sm">#{selectedUser.id} @{selectedUser.username}</div>
                <div className="mt-2 text-sm text-zinc-400">Created {formatDate(selectedUser.createdAt)}</div>
              </div>
              <div className="space-y-3">
                <div className="font-medium">Assigned Roles</div>
                <div className="space-y-2">
                  {assignedRoles.map((role) => (
                    <div key={role.roleName} className="flex items-center justify-between rounded border border-zinc-800 p-3">
                      <div>
                        <div className="font-medium">{role.roleName}</div>
                        <div className="text-xs text-zinc-500">{role.description || "No description"}</div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeRole.mutate({ userId: selectedUser.id, nextRoleName: role.roleName })}
                        aria-label="Remove role"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Select value={roleName} onValueChange={setRoleName}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {assignableRoles.map((role) => (
                        <SelectItem key={role.roleName} value={role.roleName}>
                          {role.roleName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button disabled={!roleName || assignRole.isPending} onClick={() => assignRole.mutate()}>
                    Assign
                  </Button>
                </div>
              </div>
              <div className="space-y-3">
                <div className="font-medium">Managed Partners</div>
                <div className="space-y-2">
                  {(userPartners.data?.partners ?? []).map((partner) => (
                    <div key={partner.id} className="rounded border border-zinc-800 p-3">
                      <div className="font-medium">{partner.name}</div>
                      <div className="text-xs text-zinc-500">{partner.slug}</div>
                    </div>
                  ))}
                  {userPartners.data && userPartners.data.partners.length === 0 && (
                    <div className="text-sm text-zinc-500">No managed partners</div>
                  )}
                </div>
              </div>
              <Button variant="outline" onClick={() => invalidateUserCache.mutate(selectedUser.id)}>
                Invalidate User Cache
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function RoleDialog({
  open,
  role,
  onOpenChange,
}: {
  open: boolean;
  role?: RbacRole | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const [roleName, setRoleName] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    setRoleName(role?.roleName ?? "");
    setDescription(role?.description ?? "");
  }, [role, open]);

  const mutation = useMutation({
    mutationFn: () =>
      apiRequestV2(
        role ? "PUT" : "POST",
        role ? `/api/v1/rbac/roles/${encodeURIComponent(role.roleName)}` : "/api/v1/rbac/roles",
        role ? { newRoleName: roleName.trim(), description: description.trim() || null } : { roleName: roleName.trim(), description: description.trim() || null },
      ),
    onSuccess: () => {
      toast({ title: role ? "Role updated" : "Role created" });
      invalidateRbac();
      onOpenChange(false);
    },
    onError: (error) => toast({ title: "Cannot save role", description: errorMessage(error), variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{role ? "Edit Role" : "Add Role"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Role Name</Label>
            <Input value={roleName} onChange={(event) => setRoleName(event.target.value)} placeholder="partner_admin" />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={description} onChange={(event) => setDescription(event.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!roleName.trim() || mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PermissionDialog({
  open,
  permission,
  onOpenChange,
}: {
  open: boolean;
  permission?: RbacPermission | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const [resource, setResource] = useState("");
  const [action, setAction] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    setResource(permission?.resource ?? "");
    setAction(permission?.action ?? "");
    setDescription(permission?.description ?? "");
  }, [permission, open]);

  const mutation = useMutation({
    mutationFn: () =>
      apiRequestV2(
        permission ? "PUT" : "POST",
        permission ? `/api/v1/rbac/permissions/${permission.id}` : "/api/v1/rbac/permissions",
        { resource: resource.trim(), action: action.trim(), description: description.trim() || null },
      ),
    onSuccess: () => {
      toast({ title: permission ? "Permission updated" : "Permission created" });
      invalidateRbac();
      onOpenChange(false);
    },
    onError: (error) => toast({ title: "Cannot save permission", description: errorMessage(error), variant: "destructive" }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{permission ? "Edit Permission" : "Add Permission"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Resource</Label>
            <Input value={resource} onChange={(event) => setResource(event.target.value)} placeholder="partner" />
          </div>
          <div className="space-y-2">
            <Label>Action</Label>
            <Input value={action} onChange={(event) => setAction(event.target.value)} placeholder="create" />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={description} onChange={(event) => setDescription(event.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!resource.trim() || !action.trim() || mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RolesPermissionsPage() {
  const { toast } = useToast();
  const [roleParams, setRoleParams] = useState<OuraListParams>({ page: 1, page_size: 25 });
  const [permissionParams, setPermissionParams] = useState<OuraListParams>({ page: 1, page_size: 25 });
  const [selectedRole, setSelectedRole] = useState<RbacRole | null>(null);
  const [roleDialog, setRoleDialog] = useState<RbacRole | null | undefined>();
  const [permissionDialog, setPermissionDialog] = useState<RbacPermission | null | undefined>();
  const [checkedPermissionIds, setCheckedPermissionIds] = useState<number[]>([]);
  const roles = useQuery(getRbacRolesOptions());
  const permissions = useQuery(getRbacPermissionsOptions());
  const rolePermissions = useQuery(getRbacRolePermissionsOptions(selectedRole?.roleName));
  const clearCache = useMutation(clearRbacCacheOptions());

  const roleQuery = roleParams.search?.trim().toLowerCase();
  const roleRows = (roles.data?.roles ?? []).filter((role) =>
    !roleQuery || [role.roleName, role.description ?? ""].some((value) => value.toLowerCase().includes(roleQuery)),
  );
  const permissionQuery = permissionParams.search?.trim().toLowerCase();
  const permissionRows = (permissions.data?.permissions ?? []).filter((permission) =>
    !permissionQuery ||
    [permission.permissionName, permission.resource, permission.action, permission.description ?? ""].some((value) =>
      value.toLowerCase().includes(permissionQuery),
    ),
  );

  const deleteRole = useMutation({
    mutationFn: (roleName: string) => apiRequestV2("DELETE", `/api/v1/rbac/roles/${encodeURIComponent(roleName)}`),
    onSuccess: () => {
      toast({ title: "Role deleted" });
      setSelectedRole(null);
      invalidateRbac();
    },
    onError: (error) => toast({ title: "Cannot delete role", description: errorMessage(error), variant: "destructive" }),
  });

  const deletePermission = useMutation({
    mutationFn: (permissionId: number) => apiRequestV2("DELETE", `/api/v1/rbac/permissions/${permissionId}`),
    onSuccess: () => {
      toast({ title: "Permission deleted" });
      invalidateRbac();
    },
    onError: (error) => toast({ title: "Cannot delete permission", description: errorMessage(error), variant: "destructive" }),
  });

  const assignPermissions = useMutation({
    mutationFn: () =>
      apiRequestV2("POST", `/api/v1/rbac/roles/${encodeURIComponent(selectedRole?.roleName ?? "")}/permissions`, {
        permissionIds: checkedPermissionIds,
      }),
    onSuccess: () => {
      toast({ title: "Permissions assigned" });
      setCheckedPermissionIds([]);
      invalidateRbac();
    },
    onError: (error) => toast({ title: "Cannot assign permissions", description: errorMessage(error), variant: "destructive" }),
  });

  const removePermission = useMutation({
    mutationFn: (permissionId: number) =>
      apiRequestV2("DELETE", `/api/v1/rbac/roles/${encodeURIComponent(selectedRole?.roleName ?? "")}/permissions/${permissionId}`),
    onSuccess: () => {
      toast({ title: "Permission removed from role" });
      invalidateRbac();
    },
    onError: (error) => toast({ title: "Cannot remove permission", description: errorMessage(error), variant: "destructive" }),
  });

  const assignedPermissionIds = new Set((rolePermissions.data?.permissions ?? []).map((permission) => permission.id));
  const availablePermissions = permissionRows.filter((permission) => !assignedPermissionIds.has(permission.id));

  const roleColumns = useMemo<ColumnDef<RbacRole>[]>(() => [
    { accessorKey: "roleName", header: "Role" },
    { accessorKey: "description", header: "Description", cell: ({ row }) => row.original.description || "-" },
    { accessorKey: "createdAt", header: "Created", cell: ({ row }) => formatDate(row.original.createdAt) },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <div className="flex gap-1" onClick={(event) => event.stopPropagation()}>
          <Button variant="ghost" size="icon" onClick={() => setRoleDialog(row.original)} aria-label="Edit role">
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              if (window.confirm(`Delete role "${row.original.roleName}"?`)) deleteRole.mutate(row.original.roleName);
            }}
            aria-label="Delete role"
          >
            <Trash2 className="h-4 w-4 text-red-400" />
          </Button>
        </div>
      ),
    },
  ], [deleteRole]);

  const permissionColumns = useMemo<ColumnDef<RbacPermission>[]>(() => [
    { accessorKey: "permissionName", header: "Permission", cell: ({ row }) => <Badge variant="outline">{row.original.permissionName}</Badge> },
    { accessorKey: "resource", header: "Resource" },
    { accessorKey: "action", header: "Action" },
    { accessorKey: "description", header: "Description", cell: ({ row }) => row.original.description || "-" },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <div className="flex gap-1" onClick={(event) => event.stopPropagation()}>
          <Button variant="ghost" size="icon" onClick={() => setPermissionDialog(row.original)} aria-label="Edit permission">
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              if (window.confirm(`Delete permission "${row.original.permissionName}"?`)) deletePermission.mutate(row.original.id);
            }}
            aria-label="Delete permission"
          >
            <Trash2 className="h-4 w-4 text-red-400" />
          </Button>
        </div>
      ),
    },
  ], [deletePermission]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Roles & Permissions</h2>
          <p className="text-sm text-zinc-400">Manage role definitions and permission assignments.</p>
        </div>
        <Button
          variant="outline"
          onClick={() => clearCache.mutate(undefined, {
            onSuccess: () => toast({ title: "RBAC cache cleared" }),
            onError: (error) => toast({ title: "Cannot clear cache", description: errorMessage(error), variant: "destructive" }),
          })}
        >
          Clear RBAC Cache
        </Button>
      </div>
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_420px]">
        <div className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle>Roles</CardTitle>
              <Button onClick={() => setRoleDialog(null)}><Plus className="mr-2 h-4 w-4" />Add Role</Button>
            </CardHeader>
            <CardContent>
              <AdminDataTable
                columns={roleColumns}
                data={roleRows}
                total={roleRows.length}
                params={roleParams}
                onParamsChange={setRoleParams}
                exportName="rbac-roles.csv"
                searchPlaceholder="Search roles..."
                onRowClick={setSelectedRole}
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle>Permissions</CardTitle>
              <Button onClick={() => setPermissionDialog(null)}><Plus className="mr-2 h-4 w-4" />Add Permission</Button>
            </CardHeader>
            <CardContent>
              <AdminDataTable
                columns={permissionColumns}
                data={permissionRows}
                total={permissionRows.length}
                params={permissionParams}
                onParamsChange={setPermissionParams}
                exportName="rbac-permissions.csv"
                searchPlaceholder="Search permissions..."
              />
            </CardContent>
          </Card>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>{selectedRole ? `${selectedRole.roleName} Permissions` : "Select a Role"}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {selectedRole ? (
              <>
                <div className="space-y-2">
                  {(rolePermissions.data?.permissions ?? []).map((permission) => (
                    <div key={permission.id} className="flex items-center justify-between rounded border border-zinc-800 p-3">
                      <div>
                        <Badge variant="outline">{permission.permissionName}</Badge>
                        <div className="mt-1 text-xs text-zinc-500">{permission.description || "No description"}</div>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removePermission.mutate(permission.id)} aria-label="Remove permission">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {rolePermissions.data && rolePermissions.data.permissions.length === 0 && (
                    <div className="text-sm text-zinc-500">No permissions assigned</div>
                  )}
                </div>
                <div className="space-y-3 border-t border-zinc-800 pt-4">
                  <div className="font-medium">Assign Permissions</div>
                  <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
                    {availablePermissions.map((permission) => (
                      <label key={permission.id} className="flex cursor-pointer items-center gap-3 rounded border border-zinc-800 p-3">
                        <Checkbox
                          checked={checkedPermissionIds.includes(permission.id)}
                          onCheckedChange={(checked) =>
                            setCheckedPermissionIds((current) =>
                              checked ? [...current, permission.id] : current.filter((id) => id !== permission.id),
                            )
                          }
                        />
                        <div>
                          <div className="text-sm font-medium">{permission.permissionName}</div>
                          <div className="text-xs text-zinc-500">{permission.description || "No description"}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                  <Button disabled={!checkedPermissionIds.length || assignPermissions.isPending} onClick={() => assignPermissions.mutate()}>
                    Assign Selected
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-sm text-zinc-500">Choose a role from the table to view and edit permission assignments.</div>
            )}
          </CardContent>
        </Card>
      </div>
      <RoleDialog open={roleDialog !== undefined} role={roleDialog || null} onOpenChange={(open) => !open && setRoleDialog(undefined)} />
      <PermissionDialog open={permissionDialog !== undefined} permission={permissionDialog || null} onOpenChange={(open) => !open && setPermissionDialog(undefined)} />
    </div>
  );
}

function PartnerAdminsPage() {
  const { toast } = useToast();
  const [partnerParams, setPartnerParams] = useState<OuraListParams>({ page: 1, page_size: 25 });
  const [userParams, setUserParams] = useState<OuraListParams>({ page: 1, page_size: 25 });
  const [selectedPartner, setSelectedPartner] = useState<PartnerAdmin | null>(null);
  const [selectedUserId, setSelectedUserId] = useState("");
  const partners = useQuery(getPartnerAdminListOptions({ search: partnerParams.search }));
  const users = useQuery(getRbacUsersOptions(userParams));
  const admins = useQuery(getRbacPartnerAdminsOptions(selectedPartner?.id));

  useEffect(() => {
    if (!selectedPartner && partners.data?.length) setSelectedPartner(partners.data[0]);
  }, [partners.data, selectedPartner]);

  const assign = useMutation({
    mutationFn: async () => {
      const userId = Number(selectedUserId);
      const selectedUser = users.data?.items.find((user) => user.id === userId);
      const hasPartnerAdmin = selectedUser?.roles.some((role) => role.roleName === "partner_admin");
      if (!hasPartnerAdmin) {
        await apiRequestV2("POST", `/api/v1/rbac/users/${userId}/roles`, { roleName: "partner_admin" });
      }
      return apiRequestV2("POST", "/api/v1/rbac/partner-admins", { userId, partnerId: selectedPartner?.id });
    },
    onSuccess: () => {
      toast({ title: "Partner admin assigned" });
      setSelectedUserId("");
      invalidateRbac();
    },
    onError: (error) => toast({ title: "Cannot assign partner admin", description: errorMessage(error), variant: "destructive" }),
  });

  const remove = useMutation({
    mutationFn: (userId: number) => apiRequestV2("DELETE", `/api/v1/rbac/partner-admins/${userId}/${selectedPartner?.id}`),
    onSuccess: () => {
      toast({ title: "Partner admin removed" });
      invalidateRbac();
    },
    onError: (error) => toast({ title: "Cannot remove partner admin", description: errorMessage(error), variant: "destructive" }),
  });

  const partnerColumns = useMemo<ColumnDef<PartnerAdmin>[]>(() => [
    { accessorKey: "id", header: "ID" },
    { accessorKey: "name", header: "Partner" },
    { accessorKey: "slug", header: "Slug" },
    { accessorKey: "tokenName", header: "Token", cell: ({ row }) => row.original.tokenName || "-" },
  ], []);

  const currentAdmins = admins.data?.assignedAdmins ?? [];
  const systemAdmins = admins.data?.systemAdmins ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Partner Admins</h2>
        <p className="text-sm text-zinc-400">Assign users to manage specific partners.</p>
      </div>
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_460px]">
        <Card>
          <CardHeader>
            <CardTitle>Partners</CardTitle>
          </CardHeader>
          <CardContent>
            <AdminDataTable
              columns={partnerColumns}
              data={partners.data ?? []}
              total={partners.data?.length ?? 0}
              params={partnerParams}
              onParamsChange={setPartnerParams}
              exportName="rbac-partners.csv"
              searchPlaceholder="Search partners..."
              onRowClick={setSelectedPartner}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{selectedPartner ? `${selectedPartner.name} Admins` : "Select a Partner"}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {selectedPartner ? (
              <>
                <div className="space-y-3">
                  <Label>Search Users</Label>
                  <Input
                    value={userParams.search ?? ""}
                    onChange={(event) => setUserParams((current) => ({ ...current, page: 1, search: event.target.value }))}
                    placeholder="Search username, name, id..."
                  />
                  <div className="flex gap-2">
                    <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select user" />
                      </SelectTrigger>
                      <SelectContent>
                        {(users.data?.items ?? []).map((user) => (
                          <SelectItem key={user.id} value={String(user.id)}>
                            #{user.id} @{user.username} {user.name ? `- ${user.name}` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button disabled={!selectedUserId || assign.isPending} onClick={() => assign.mutate()}>
                      Assign
                    </Button>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="font-medium">Assigned Admins</div>
                  {currentAdmins.map((admin) => (
                    <div key={admin.userId} className="flex items-center justify-between rounded border border-zinc-800 p-3">
                      <div>
                        <div className="font-medium">@{admin.username}</div>
                        <div className="text-xs text-zinc-500">{admin.name || "No display name"} - assigned {formatDate(admin.assignedAt)}</div>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => remove.mutate(admin.userId)} aria-label="Remove partner admin">
                        <Trash2 className="h-4 w-4 text-red-400" />
                      </Button>
                    </div>
                  ))}
                  {currentAdmins.length === 0 && <div className="text-sm text-zinc-500">No direct partner admins assigned</div>}
                </div>
                <div className="space-y-2 border-t border-zinc-800 pt-4">
                  <div className="font-medium">System Admin Access</div>
                  {systemAdmins.map((admin) => (
                    <div key={admin.userId} className="rounded border border-zinc-800 p-3">
                      <div className="font-medium">@{admin.username}</div>
                      <div className="text-xs text-zinc-500">Can manage every partner</div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-sm text-zinc-500">Choose a partner to manage assignments.</div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function WhitelistDialog({
  open,
  roles,
  onOpenChange,
}: {
  open: boolean;
  roles: RbacRole[];
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const [identifier, setIdentifier] = useState("");
  const [assignedRoleName, setAssignedRoleName] = useState("");
  const [bulkText, setBulkText] = useState("");
  const single = useMutation({
    mutationFn: () => apiRequestV2("POST", "/api/v1/rbac/whitelist", { identifier: identifier.trim(), assignedRoleName }),
    onSuccess: () => {
      toast({ title: "Whitelist entry created" });
      invalidateRbac();
      onOpenChange(false);
    },
    onError: (error) => toast({ title: "Cannot create whitelist entry", description: errorMessage(error), variant: "destructive" }),
  });
  const bulk = useMutation(createBulkWhitelistOptions());

  const submitBulk = () => {
    const entries = bulkText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [nextIdentifier, nextRoleName] = line.split(",").map((part) => part.trim());
        return { identifier: nextIdentifier, assignedRoleName: nextRoleName };
      })
      .filter((entry) => entry.identifier && entry.assignedRoleName);

    if (!entries.length) {
      toast({ title: "No valid rows", description: "Use one row per entry: identifier,roleName", variant: "destructive" });
      return;
    }

    bulk.mutate({ entries }, {
      onSuccess: (result) => {
        toast({ title: "Bulk import complete", description: `${result.successCount} created, ${result.failedCount} failed` });
        onOpenChange(false);
      },
      onError: (error) => toast({ title: "Cannot import whitelist", description: errorMessage(error), variant: "destructive" }),
    });
  };

  function submitSingle(event: FormEvent) {
    event.preventDefault();
    single.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add Whitelist Entry</DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={submitSingle}>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Identifier</Label>
              <Input value={identifier} onChange={(event) => setIdentifier(event.target.value)} placeholder="username or email" />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={assignedRoleName} onValueChange={setAssignedRoleName}>
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.roleName} value={role.roleName}>
                      {role.roleName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button disabled={!identifier.trim() || !assignedRoleName || single.isPending} type="submit">
            Create Single Entry
          </Button>
        </form>
        <div className="space-y-3 border-t border-zinc-800 pt-4">
          <Label>Bulk Import</Label>
          <Textarea
            value={bulkText}
            onChange={(event) => setBulkText(event.target.value)}
            placeholder={"user_one,partner_admin\nuser_two,system_admin"}
            rows={7}
          />
          <Button variant="outline" disabled={!bulkText.trim() || bulk.isPending} onClick={submitBulk}>
            Import Bulk Entries
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WhitelistPage() {
  const { toast } = useToast();
  const [params, setParams] = useState<OuraListParams>({ page: 1, page_size: 25 });
  const [dialogOpen, setDialogOpen] = useState(false);
  const whitelist = useQuery(getRbacWhitelistOptions());
  const roles = useQuery(getRbacRolesOptions());
  const query = params.search?.trim().toLowerCase();
  const rows = (whitelist.data?.whitelist ?? []).filter((entry) =>
    !query || [entry.identifier, entry.assignedRoleName].some((value) => value.toLowerCase().includes(query)),
  );
  const del = useMutation({
    mutationFn: (id: number) => apiRequestV2("DELETE", `/api/v1/rbac/whitelist/${id}`),
    onSuccess: () => {
      toast({ title: "Whitelist entry deleted" });
      invalidateRbac();
    },
    onError: (error) => toast({ title: "Cannot delete whitelist entry", description: errorMessage(error), variant: "destructive" }),
  });

  const columns = useMemo<ColumnDef<RbacWhitelistEntry>[]>(() => [
    { accessorKey: "identifier", header: "Identifier" },
    { accessorKey: "assignedRoleName", header: "Role", cell: ({ row }) => <Badge variant="outline">{row.original.assignedRoleName}</Badge> },
    { accessorKey: "createdAt", header: "Created", cell: ({ row }) => formatDate(row.original.createdAt) },
    {
      accessorKey: "processedAt",
      header: "Status",
      cell: ({ row }) => row.original.processedAt ? <Badge>Processed</Badge> : <Badge variant="outline">Pending</Badge>,
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            if (window.confirm(`Delete whitelist entry for "${row.original.identifier}"?`)) del.mutate(row.original.id);
          }}
          aria-label="Delete whitelist entry"
        >
          <Trash2 className="h-4 w-4 text-red-400" />
        </Button>
      ),
    },
  ], [del]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Role Whitelist</h2>
        <p className="text-sm text-zinc-400">Pre-assign roles for users by identifier before signup or account linking.</p>
      </div>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Whitelist Entries</CardTitle>
          <Button onClick={() => setDialogOpen(true)}><Plus className="mr-2 h-4 w-4" />Add Entry</Button>
        </CardHeader>
        <CardContent>
          <AdminDataTable
            columns={columns}
            data={rows}
            total={rows.length}
            params={params}
            onParamsChange={setParams}
            exportName="rbac-whitelist.csv"
            searchPlaceholder="Search identifier or role..."
          />
        </CardContent>
      </Card>
      <WhitelistDialog open={dialogOpen} roles={roles.data?.roles ?? []} onOpenChange={setDialogOpen} />
    </div>
  );
}

export function RbacAdminPage() {
  const [location] = useLocation();

  if (location === "/tweeto-admin/rbac" || location === "/tweeto-admin/rbac/") {
    return <Redirect to="/tweeto-admin/rbac/users" />;
  }

  return (
    <div className="space-y-6">
      <SectionNav />
      {location.endsWith("/users") && <UsersAndRolesPage />}
      {location.endsWith("/roles") && <RolesPermissionsPage />}
      {location.endsWith("/partners") && <PartnerAdminsPage />}
      {location.endsWith("/whitelist") && <WhitelistPage />}
      {!rbacNav.some((item) => location === item.href) && <Redirect to="/tweeto-admin/rbac/users" />}
    </div>
  );
}
